import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import ReactInputMask from "react-input-mask";

import DashboardLayout from "./components/Layout";
import useRole, { OWNER_TICKET_TYPES } from "./components/useRole";

import { getEventByIdAdmin, getEventSeatMap } from "../../api/plan";
import { createTicketAdmin, getTicketById, updateTicket } from "../../api/subscription";
import { buildSeatPricing } from "../../components/SeatMap/pricing";
import { seatKey, unitLabel } from "../../components/SeatMap";
import SeatPickerModal, { MAX_SEATS_PER_ORDER } from "../../components/SeatPickerModal";


export const paidBy = ['direct', 'cash', 'cardtocard', 'invitation', 'cooperative', 'other'];
// Tickets handed out rather than sold: counted, but never added to revenue
export const unpaidTypes = ['invitation', 'cooperative'];

const TicketsFormPage = () => {
  const { eventId, id } = useParams();
  const { isAdmin, canIssueTickets, canEditTicket } = useRole();
  // The organizer hands out invitations and partner tickets; the admin writes any
  const paymentForms = isAdmin ? paidBy : OWNER_TICKET_TYPES;
  const [eventData, setEventData] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [seatMap, setSeatMap] = useState(null);
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  // Seats this ticket covers. Arriving from the seat plan ("Bilet yarat") they
  // are already chosen; otherwise they are picked on the map here.
  const [selectedSeats, setSelectedSeats] = useState(location.state?.seats || []);
  const [isSeatPickerOpen, setIsSeatPickerOpen] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { isSubmitting, isValid, errors },
  } = useForm();

  const isSeatMode = !!(eventData?.data?.hasSeatSelection && eventData?.data?.venueId);
  // The event's own question for buyers (Parametrlər -> Əlavə sual), if it has one
  const customField = eventData?.data?.customField?.label && eventData?.data?.customField?.options?.length
    ? eventData.data.customField
    : null;
  const selectedTicketId = watch("ticket");
  const selectedType = (eventData?.data?.ticketTypes || []).find(({ _id }) => _id === selectedTicketId);
  // Seats decide how many tickets an order is, so the count is only asked for
  // when there are none. A table is already a group, so it is issued one by one.
  const canChooseCount = !isEdit
    && !!selectedTicketId
    && selectedSeats.length === 0
    && !(selectedType?.isTable && selectedType?.capacity > 1);

  const seatPricing = useMemo(
    () => buildSeatPricing(seatMap?.venue, eventData?.data?.ticketTypes, eventData?.data?.seatPlan),
    [seatMap, eventData]
  );

  // The ticket being edited keeps its own seats selectable on the map
  const pickerSeatMap = useMemo(() => {
    if (!seatMap) return null;
    const own = new Set((ticketData?.seats?.length
      ? ticketData.seats
      : (ticketData?.seatNumber ? [{ zone: ticketData.seatZone, number: ticketData.seatNumber }] : [])
    ).map(({ zone, number }) => seatKey(zone, number)));
    if (!own.size) return seatMap;
    return {
      ...seatMap,
      takenSeats: (seatMap.takenSeats || []).filter(({ zone, number }) => !own.has(seatKey(zone, number))),
    };
  }, [seatMap, ticketData]);

  const seatLabelOf = ({ zone, number }) => {
    const zoneData = seatMap?.venue?.zones?.find((item) => item.code === zone);
    return `${zoneData?.name || zone} · ${unitLabel(zoneData, number)}`;
  };

  const getEventData = async (eventId) => {
    const response = await getEventByIdAdmin(eventId); // get plan data instead
    setEventData(response?.data);
  };

  useEffect(() => {
    getEventData(eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (isSeatMode) {
      getEventSeatMap(eventId)
        .then((res) => setSeatMap(res?.data?.data))
        .catch(() => setSeatMap(null));
    }
  }, [isSeatMode, eventId]);

  // Choosing another ticket type drops seats that it does not sell
  const previousTicketRef = useRef();
  useEffect(() => {
    if (previousTicketRef.current !== undefined && previousTicketRef.current !== selectedTicketId) {
      setSelectedSeats((seats) => (seats.some((seat) => seat.ticketTypeId !== selectedTicketId) ? [] : seats));
    }
    previousTicketRef.current = selectedTicketId;
  }, [selectedTicketId]);

  // Seats handed over by the seat plan decide the ticket type
  useEffect(() => {
    const fromPlan = location.state?.seats;
    if (fromPlan?.length) setValue("ticket", fromPlan[0].ticketTypeId, { shouldValidate: true });
  }, [location.state, setValue]);

  useEffect(() => {
    if (!isEdit && !isAdmin) setValue("paidBy", OWNER_TICKET_TYPES[0], { shouldValidate: true });
  }, [isEdit, isAdmin, setValue]);

  useEffect(() => {
    if (isEdit) {
      getTicketById(eventId, id).then((res) => {
        const data = res?.data?.data;
        setTicketData(data);
        if (data) {
          setValue("ticket", data.ticketTypeId?._id);
          setValue("name", data.name);
          setValue("email", data.email);
          setValue("phone", data.phone);
          setValue("paidBy", data.paidBy);
          setSelectedSeats(data.seats?.length
            ? data.seats.map(({ zone, number }) => ({ zone, number, ticketTypeId: data.ticketTypeId?._id }))
            : (data.seatNumber ? [{ zone: data.seatZone, number: data.seatNumber, ticketTypeId: data.ticketTypeId?._id }] : []));
          setValue("customValue", data.customFieldValue || "");
        }
      });
    }
  }, [isEdit, setValue, eventId, id]);

  const onSubmit = async (formData) => {
    const seats = selectedSeats.map(({ zone, number }) => ({ zone, number }));
    const payload = {
      ticket: formData.ticket,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      paidBy: formData.paidBy,
      willSendEmail: formData.willSendEmail,
      // '' clears the seats of an edited ticket; nothing is sent when there is nothing to change
      seatNumber: isSeatMode && !seats.length ? (ticketData?.seatNumber ? "" : undefined) : undefined,
      seats: isSeatMode && seats.length ? seats : undefined,
      quantity: canChooseCount ? Number(formData.quantity || 1) : undefined,
      // '' clears the answer of an edited ticket; nothing is sent when there is nothing to change
      customValue: customField ? (formData.customValue || (isEdit ? "" : undefined)) : undefined,
    };

    try {
      if (isEdit) {
          await updateTicket(eventId, id, payload);
      } else {
          await createTicketAdmin(eventId, payload);
      }
    } catch (error) {
      setError("root.serverError", { type: "server", message: error?.response?.data?.message || "Yadda saxlamaq olmadı." });
      return;
    }

    navigate(`/dashboard/${eventId}/tickets`);
  };
  if (!canIssueTickets) return <Navigate to={`/dashboard/${eventId}`} replace />;
  // Someone else's ticket: readable in the list, not changeable here
  if (isEdit && ticketData?._id && !canEditTicket(ticketData)) {
    return <Navigate to={`/dashboard/${eventId}/tickets`} replace />;
  }


  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-2xl mx-auto mt-10 bg-white p-8 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-6">
          {isEdit ? "📄 Bileti Düzəlt" : "➕ Yeni Bilet Əlavə Et"}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <select
            id="ticket"
            {...register("ticket", { required: true })}
            className="w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option disabled value="">Bilet növü seçin</option>
            {eventData?.data?.ticketTypes?.map(({_id, name}) => <option key={_id} value={_id}>{name?.az}</option>)}
          </select>

          {isSeatMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Yerlər</label>
              {selectedSeats.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {selectedSeats.map((seat) => (
                    <span
                      key={seatKey(seat.zone, seat.number)}
                      className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-sm text-gray-900"
                    >
                      {seatLabelOf(seat)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-500">Yersiz bilet</p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setIsSeatPickerOpen(true)}
                  disabled={!seatMap}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-white"
                >
                  🗺️ Xəritədən yer seç
                </button>
                {selectedSeats.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSeats([])}
                    className="rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Yerləri sil
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Ən çox {MAX_SEATS_PER_ORDER} yer; seçilən yerlərin sayı qədər bilet yaranır və hamısı bir e-poçtla gedir.
              </p>
            </div>
          )}

          {canChooseCount && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Bilet sayı</label>
              <select
                {...register("quantity")}
                className="mt-1 w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
              >
                {[1, 2, 3, 4, 5].map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Bir neçə bilet bir e-poçtla gedir; hər biletin öz QR kodu olur və ayrıca paylaşıla bilər.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Ad</label>
            <input
              {...register("name", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              {...register("email", { required: true })}
              className="mt-1 w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Telefon</label>
            <Controller
              control={control}
              name="phone"
              render={() => (
                <ReactInputMask
                  mask="099-999-9999"
                  id="phone"
                  {...register("phone", {
                    required: true,
                    pattern: /0\d\d-\d\d\d-\d\d\d\d/
                  })}
                  placeholder="(0XX) XXX-XX-XX"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              )}
            />
          </div>

          <select
            id="paidBy"
            {...register("paidBy", { required: true })}
            className="w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option disabled value="">Ödəmə forması seçin</option>
            {paymentForms?.map(item => <option key={item} value={item}>{item}</option>)}
          </select>

          {customField && (
            <div>
              <label htmlFor="customValue" className="block text-sm font-medium text-gray-700">{customField.label}</label>
              <Controller
                name="customValue"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <select
                    {...field}
                    id="customValue"
                    value={field.value ?? ""}
                    className="mt-1 w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="">Seçilməyib</option>
                    {customField.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                )}
              />
            </div>
          )}

          <div className="w-full items-center pt-2 pb-1">
            <div className="flex items-center">
              <input {...register("willSendEmail", {required: false})} id="checked-checkbox-agreement" type="checkbox" className="w-4 h-4 text-blue-700 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2" />
              <label className="ms-2 text-sm font-medium text-gray-800">Email göndərilsin?</label>
            </div>
          </div>

          {errors?.root?.serverError && (
            <p className="text-sm text-red-600">{errors.root.serverError.message}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !isValid}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
          >
            {isEdit ? "Yadda saxla" : "Əlavə et"}
          </button>
        </form>
      </div>

      {isSeatPickerOpen && (
        <SeatPickerModal
          seatMap={pickerSeatMap}
          seatPricing={seatPricing}
          initialSeats={selectedSeats}
          onConfirm={(seats) => {
            setSelectedSeats(seats);
            if (seats[0]?.ticketTypeId) setValue("ticket", seats[0].ticketTypeId, { shouldValidate: true });
            setIsSeatPickerOpen(false);
          }}
          onClose={() => setIsSeatPickerOpen(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default TicketsFormPage;
